import React from 'react';
import { formatterCurrency } from '../../../utils/methods';
import TicketInfoCell from './TicketInfoCell';
import SellerNameCell from '../../common/SellerNameCell';
import { MemberStatusBadge } from './MemberListStyled';
import { formatVisitPath } from '../../../constants/visitPath';

/** 회원목록 컬럼 설정 저장 키(branch_table_column_setting.TABLE_KEY) */
export const MEMBER_LIST_TABLE_KEY = 'member-list';

const point = value => `${Number(value || 0).toLocaleString()} P`;
// 기간 필터가 걸려 있으면 컬럼 값도 그 기간 기준(백엔드 동기화)이라 헤더에 '검색한' 을 붙여 표기한다
const periodLabel = (ctx, from, to, base, searched) => (from && to ? ctx.t(searched) : ctx.t(base));

/**
 * 회원목록 컬럼 카탈로그. 배열 순서 = 기본 순서, pinned = 기본 왼쪽 고정.
 * 체크박스 열은 설정 대상이 아니라 여기 없다(항상 맨 왼쪽 고정).
 *
 *  key       저장 키. 바꾸면 지점들이 저장해 둔 설정에서 그 컬럼이 사라지므로 바꾸지 말 것
 *  label     i18n 키(설정 팝오버·엑셀 헤더에도 쓴다)
 *  hint      헤더·설정 팝오버 툴팁. 산식이 헷갈리는 컬럼은 반드시 적는다
 *  sortField 정렬 가능 컬럼의 서버 sortBy 값
 *  header    (ctx) => 라벨. 기간에 따라 라벨이 바뀌는 컬럼만
 *  render    (member, ctx) => 셀 내용
 *  excel     (member, ctx) => 엑셀 값
 *  insertAfter 설정을 저장해 둔 지점에 새 컬럼이 처음 나타날 자리(이 키 바로 뒤). 없으면 맨 뒤
 */
export const MEMBER_LIST_COLUMNS = [
  {
    key: 'memberType',
    label: 'Client Status',
    hint: '보유 상품 상태로 판정합니다. 유효회원 > 사용예정 > 중지회원 > 만료회원 순.',
    pinned: true,
    render: (m, ctx) => {
      const label = ctx.badgeLabel || m.memberType;
      return <MemberStatusBadge memberType={label}>{label}</MemberStatusBadge>;
    },
    excel: (m, ctx) => ctx.badgeLabel || m.memberType || '',
    excelAlign: 'center',
  },
  {
    key: 'name',
    label: 'Name',
    pinned: true,
    sortField: 'name',
    render: m => m.name || '-',
    excel: m => m.name || '',
    excelAlign: 'left',
  },
  {
    key: 'gender',
    label: 'Gender',
    render: (m, ctx) => (m.gender ? ctx.t(m.gender) : '-'),
    excel: m => (m.gender === 'MALE' ? '남' : m.gender === 'FEMALE' ? '여' : m.gender || ''),
    excelAlign: 'center',
  },
  {
    key: 'age',
    label: 'Age',
    sortField: 'age',
    render: m => m.age || '-',
    excel: m => m.age || '',
    excelAlign: 'center',
  },
  {
    key: 'phoneNumber',
    label: 'Phone Number',
    render: m => m.phoneNumber || '-',
    excel: m => m.phoneNumber || '',
    excelAlign: 'left',
  },
  {
    // 백로그 #170 — 지점별 등록일(member_branch_profile.REGISTERED_DT), 없으면 그 지점 첫 결제일
    key: 'branchRegisteredDate',
    label: 'Registration date​​',
    hint: '이 지점에 처음 등록한 날. 잠재회원 전환 시 잠재회원 등록일을 승계하고, 회원 상세에서 지점이 고칠 수 있다. 값이 없으면 이 지점 첫 결제일로 표시한다.',
    render: m => m.branchRegisteredDate || '-',
    excel: m => m.branchRegisteredDate || '',
    excelAlign: 'center',
  },
  {
    key: 'firstPaymentDate',
    label: 'First Payment Date',
    hint: '조회 지점 기준 첫 결제(판매등록)일.',
    sortField: 'firstPaymentDate',
    render: m => m.firstPaymentDate || '-',
    excel: m => m.firstPaymentDate || '',
    excelAlign: 'center',
  },
  {
    key: 'lastPaymentDate',
    label: 'Last Payment Date',
    sortField: 'lastPaymentDate',
    render: m => m.lastPaymentDate || '-',
    excel: m => m.lastPaymentDate || '',
    excelAlign: 'center',
  },
  {
    key: 'totalPaymentAmount',
    label: 'Total Payment Amount',
    hint: '가장 최근 결제 1건의 금액입니다. 누적 결제액이 아닙니다.',
    sortField: 'totalPaymentAmount',
    render: m => (m.totalPaymentAmount ? formatterCurrency(m.totalPaymentAmount) : '-'),
    excel: m => (m.totalPaymentAmount ? `${Number(m.totalPaymentAmount).toLocaleString('ko-KR')}원` : ''),
    excelAlign: 'right',
  },
  {
    key: 'ticketInfo',
    label: 'Ticket Info',
    align: 'left',
    cellSx: { padding: '8px 12px', verticalAlign: 'top', whiteSpace: 'nowrap' },
    render: (m, ctx) =>
      m.tickets?.length > 0 ? <TicketInfoCell tickets={m.tickets} currentBranchId={ctx.currentBranchId} /> : '-',
    // 줄바꿈·슬래시·콤마는 엑셀 셀에서 깨져 공백으로 정리한다
    excel: m =>
      (m.ticketName || '')
        .replace(/\n/g, '  ')
        .replace(/[,\/\\|]/g, ' ')
        .replace(/\s+/g, '  ')
        .trim(),
    excelAlign: 'left',
  },
  {
    key: 'unUsedCount',
    label: 'Total Remaining Lessons',
    hint: '보유 수강권 잔여 횟수의 합.',
    sortField: 'unUsedCount',
    render: m => m.unUsedCount ?? '-',
    excel: m => m.unUsedCount || '',
    excelAlign: 'center',
  },
  {
    key: 'lastExpireDate',
    label: 'Last Expiry Date',
    hint: '보유 상품 중 가장 늦은 만료일.',
    sortField: 'lastExpireDate',
    render: m => m.lastExpireDate || '-',
    excel: m => m.lastExpireDate || '',
    excelAlign: 'center',
  },
  {
    key: 'lockerExpireDate',
    label: 'Locker Expiry Date',
    sortField: 'lockerExpireDate',
    render: m => m.lockerExpireDate || '-',
    excel: m => m.lockerExpireDate || '',
    excelAlign: 'center',
  },
  {
    key: 'lastAttendDate',
    label: 'Recent Visit',
    hint: '센터 입장기록과 수업 출석 중 가장 최근 날짜.',
    sortField: 'lastAttendDate',
    render: m => m.lastAttendDate || '-',
    excel: m => m.lastAttendDate || '',
    excelAlign: 'center',
  },
  {
    key: 'attendCount',
    label: 'Cumulative Total Visits',
    hint: '센터 입장(게이트·키오스크·체크인) + 예약 수업 출석. 하루에 여러 번 와도 1회. 상세필터 기간을 걸면 그 기간 기준.',
    sortField: 'attendCount',
    header: ctx => periodLabel(ctx, ctx.appliedQuery?.visitDateFrom, ctx.appliedQuery?.visitDateTo, 'Cumulative Total Visits', '검색한 누적 방문 수'),
    render: m => m.attendCount ?? '-',
    excel: m => m.attendCount || '',
    excelAlign: 'center',
  },
  {
    key: 'classAttendCount',
    label: '수업 출석',
    hint: '예약된 수업에 출석 처리된 횟수(수업 단위, 같은 날 2수업이면 2회). 수업 없이 센터만 들른 날은 세지 않습니다. 상세필터 기간을 걸면 그 기간 기준.',
    sortField: 'classAttendCount',
    header: ctx => periodLabel(ctx, ctx.appliedQuery?.classAttendDateFrom, ctx.appliedQuery?.classAttendDateTo, '수업 출석', '검색한 수업 출석'),
    render: m => m.classAttendCount ?? '-',
    excel: m => m.classAttendCount ?? '',
    excelAlign: 'center',
  },
  {
    key: 'classAbsentCount',
    label: '수업 결석',
    hint: '예약된 수업에 결석 처리된 횟수(수업 단위). 취소된 예약은 세지 않습니다. 상세필터 기간을 걸면 그 기간 기준.',
    sortField: 'classAbsentCount',
    header: ctx => periodLabel(ctx, ctx.appliedQuery?.classAbsentDateFrom, ctx.appliedQuery?.classAbsentDateTo, '수업 결석', '검색한 수업 결석'),
    render: m => m.classAbsentCount ?? '-',
    excel: m => m.classAbsentCount ?? '',
    excelAlign: 'center',
  },
  // 포인트 3종 — 조회 지점 기준(본사는 산하 전체). 누적·사용은 회원상세 총 적립/총 차감과 같은 순액
  {
    key: 'earnedPoint',
    label: '누적 포인트',
    hint: '조회 지점 기준 총 적립(취소분 제외).',
    sortField: 'earnedPoint',
    render: m => point(m.earnedPoint),
    excel: m => m.earnedPoint ?? 0,
    excelAlign: 'right',
  },
  {
    key: 'usedPoint',
    label: '사용 포인트',
    hint: '조회 지점 기준 총 차감(취소분 제외).',
    sortField: 'usedPoint',
    render: m => point(m.usedPoint),
    excel: m => m.usedPoint ?? 0,
    excelAlign: 'right',
  },
  {
    key: 'pointBalance',
    label: '잔여 포인트',
    sortField: 'pointBalance',
    cellSx: { fontWeight: 600 },
    render: m => point(m.pointBalance),
    excel: m => m.pointBalance ?? 0,
    excelAlign: 'right',
  },
  {
    key: 'managerName',
    label: 'Instructor',
    hint: '가장 최근 결제 건의 판매담당자.',
    render: m => <SellerNameCell managerName={m.managerName} />,
    excel: m => m.managerName || '',
    excelAlign: 'left',
  },
  {
    // key 는 구 '유입경로' 컬럼의 것을 유지한다 — 지점별로 저장된 위치·표시 설정을 그대로 잇기 위해서다
    key: 'inflow',
    label: '방문경로',
    hint: '이 지점의 방문경로. 잠재회원 전환 시 승계되거나 회원 상세에서 지정한다.',
    render: m => formatVisitPath(m.visitPath, m.visitPathEtc),
    excel: m => (m.visitPath ? formatVisitPath(m.visitPath, m.visitPathEtc) : ''),
    excelAlign: 'left',
  },
  {
    key: 'isSmsReceive',
    label: 'Acception SMS',
    render: m => m.isSmsReceive || '-',
    excel: m => (m.isSmsReceive === 'Y' ? 'O' : m.isSmsReceive === 'N' ? 'X' : m.isSmsReceive || ''),
    excelAlign: 'center',
  },
  {
    // 2026-10-22 — 로그인 지점 기준 연동(LINKED) 여부. 회원 상세 연동 줄과 같은 기준
    key: 'appLinked',
    label: '앱 연동',
    insertAfter: 'isSmsReceive',
    hint: '로그인한 지점과 앱 연동된 회원인지. 다지점 이용권으로 이 지점을 쓸 수 있어도 연동한 건 아닙니다.',
    render: m => (m.appLinked === 'Y' ? '연동' : '미연동'),
    excel: m => (m.appLinked === 'Y' ? '연동' : '미연동'),
    excelAlign: 'center',
  },
];
